import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import Reveal from '../../common/Reveal';

const AUTO_SLIDE_MS = 6000;

function CheckIcon() {
  return (
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#53BD0C]">
      <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </span>
  );
}

function ArrowButton({ direction, onClick }) {
  const isPrev = direction === 'prev';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isPrev ? 'Previous offer' : 'Next offer'}
      className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#9369CC] bg-white text-[#9369CC] shadow-sm transition hover:bg-[#9369CC] hover:text-white"
    >
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d={isPrev ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'} />
      </svg>
    </button>
  );
}

function OfferCard({ offer }) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = offer.imageUrl && !imageFailed;

  return (
    <article className="offer-card-enter rounded-[32px] bg-gradient-to-br from-[#C9A8F5] via-[#E9DDFB] to-[#A9D6F2] p-[1.5px] shadow-[0_24px_60px_rgba(147,105,204,0.18)]">
      <div
        className={`group relative grid items-center gap-8 overflow-hidden rounded-[30px] bg-gradient-to-br from-[#FBF8FF] via-white to-[#F1F8FD] p-5 sm:p-8 lg:gap-10 lg:p-8 ${
          showImage ? 'lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.6fr)]' : ''
        }`}
      >
        <div className="relative z-10 text-center lg:text-left">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#9369CC]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[#9369CC] ring-1 ring-[#9369CC]/25 sm:text-sm">
            <span className="relative flex h-2 w-2">
              <span className="absolute inset-0 animate-ping rounded-full bg-[#B539FD] opacity-70" />
              <span className="relative h-2 w-2 rounded-full bg-[#B539FD]" />
            </span>
            Limited Offer
          </span>

          {offer.title ? (
            <h3 className="mt-5 bg-gradient-to-r from-[#1B1B1B] via-[#4B2A7B] to-[#9369CC] bg-clip-text text-[28px] font-bold leading-tight text-transparent sm:text-4xl lg:text-[40px]">
              {offer.title}
            </h3>
          ) : null}

          {offer.subtitle ? (
            <p className="mt-3 text-sm font-medium leading-relaxed text-[#4B4B4B] sm:text-base lg:text-base text-justify text-wrap ">{offer.subtitle}</p>
          ) : null}

          {offer.pointers?.length ? (
            <ul className="mx-auto mt-6 grid max-w-md gap-3 text-left lg:mx-0">
              {offer.pointers.map((pointer) => (
                <li key={pointer.id} className="flex items-start gap-3 text-base font-medium text-[#1B1B1B]">
                  <CheckIcon />
                  <span className="leading-snug">{pointer.text}</span>
                </li>
              ))}
            </ul>
          ) : null}

          <Link
            to={ROUTES.PRICING}
            className="mt-8 inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-[#9369CC] to-[#B539FD] py-1.5 pl-6 pr-1.5 text-base font-semibold text-white shadow-[0_10px_24px_rgba(147,105,204,0.35)] transition hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(147,105,204,0.45)]"
          >
            Grab the Offer
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#9369CC] transition-transform group-hover:translate-x-0.5">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </Link>
        </div>

        {showImage ? (
          <div className="relative z-10 order-first lg:order-none">
            <span
              className="pointer-events-none absolute -inset-3 rounded-[28px] bg-gradient-to-br from-[#B539FD]/25 via-transparent to-[#1A87CC]/25 blur-2xl"
              aria-hidden
            />
            <img
              src={offer.imageUrl}
              alt={offer.title || 'Offer'}
              loading="lazy"
              decoding="async"
              onError={() => setImageFailed(true)}
              className="relative block h-auto w-full rounded-2xl shadow-[0_18px_40px_rgba(27,27,27,0.22)] ring-1 ring-black/5 transition-transform duration-500 group-hover:scale-[1.015]"
            />
          </div>
        ) : null}
      </div>
    </article>
  );
}

function OffersSection({ offers }) {
  const list = Array.isArray(offers) ? offers : [];
  const total = list.length;
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (activeIndex >= total) setActiveIndex(0);
  }, [activeIndex, total]);

  useEffect(() => {
    if (total <= 1 || paused) return undefined;
    const timer = window.setInterval(() => {
      setActiveIndex((index) => (index + 1) % total);
    }, AUTO_SLIDE_MS);
    return () => window.clearInterval(timer);
  }, [total, paused]);

  if (!total) return null;

  const current = list[activeIndex] || list[0];
  const goPrev = () => setActiveIndex((index) => (index - 1 + total) % total);
  const goNext = () => setActiveIndex((index) => (index + 1) % total);

  return (
    <section className="px-4 py-10 sm:px-6 lg:px-8 lg:py-14" aria-label="Offers">
      <div className="mx-auto max-w-7xl">
        <Reveal variant="up">
          <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
            <OfferCard key={current.id} offer={current} />
          </div>
        </Reveal>

        {total > 1 ? (
          <div className="mt-6 flex items-center justify-center gap-4">
            <ArrowButton direction="prev" onClick={goPrev} />
            <div className="flex items-center gap-2">
              {list.map((offer, index) => (
                <button
                  key={offer.id ?? index}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  aria-label={`Go to offer ${index + 1}`}
                  className={`h-2.5 rounded-full transition-all ${
                    activeIndex === index ? 'w-8 bg-[#9369CC]' : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
            <ArrowButton direction="next" onClick={goNext} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default OffersSection;
